import Range, {
  IAdjacentValues, EXCEEDED_PRECISION_MESSAGE,
  exceedsMaximumPrecision,
} from './index';
import Decimal from 'decimal.js';
import InvalidInputError from '../../../Errors/InvalidInputError';
import DecimalPrecisionError from '../../../Errors/DecimalPrecisionError';

/**
 * Interface that represents the values returned with contains method on Range and Ranges Types
 */
export interface IRangeContainsResponse {
  /**
   * @property contains - boolean value to represent if the value is contained in the Range(s).
   * @property adjacentValues - The adjacent values in the Range(s) if the searched value is not in the Range(s);
   */
  contains: boolean;
  adjacentValues?: IAdjacentValues|undefined;

}

/**
 * Value that represents a collection of numeric Ranges
 */
export default class Ranges {
  /**
   * @property ranges - Collection of Range types
   */
  public ranges : Range[];

  /**
   * Creates a new Range model.
   * @param ranges - Collection of Range types
   */
  constructor(
    ranges: Range[],
  ) {
    if (ranges === undefined || ranges.length === 0) {
      throw new InvalidInputError('There collection of ranges should have at least one range defined');
    }
    this.ranges = ranges;
  }

  /**
   * Getter for ranges
   */
  public getRanges(): Range[] {
    return this.ranges;
  }

  /**
   * Whether this range contains the given value.
   * @param value {Range|Number|String} - The value which may be contained by this range.
   * @returns {IRangeContainsResponse} with contains true when the value is within the range, otherwise contains false with adjacent values.
   * @throws InvalidInputError if the input value is not valid
   * @throws DecimalPrecisionError if the input has more than the specified precision
   */
  public contains(value: string|number|Decimal): IRangeContainsResponse {
    let valueBigNum: Decimal;
    let isContainedBy: boolean = false;
    try {
      valueBigNum = new Decimal(value);
    } catch {
      throw new InvalidInputError(`The value "${value}" is not a valid input`);
    }
    if (valueBigNum !== undefined) {
      if (exceedsMaximumPrecision(valueBigNum)) {
        throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
      }
      for (let i = 0 ; i < this.ranges.length; i++) {
        const range = this.ranges[i];
        if (range.contains(valueBigNum)) {
          isContainedBy = true;
          break;
        }
      }
    } else {
      throw new InvalidInputError('A valid value is required to check if the range contains it');
    }
    const rangeContains: IRangeContainsResponse = { contains : isContainedBy };
    if (!isContainedBy) {
      rangeContains.adjacentValues = this.calculateNearestValues(valueBigNum);
    }
    return rangeContains;
  }

  /**
   * Method to find the adjacent values in Ranges type for the input value.
   * @param valueBigNum {Decimal} - The value for which the adjacent values are being calculated.
   * @returns {IAdjacentValues} The values in Ranges that are adjacent to the input value.
   */
  private calculateNearestValues(valueBigNum: Decimal) : IAdjacentValues  {
    const adjacentValues: IAdjacentValues = {};
    for (const range of this.ranges) {
      const tempAdjValues = range.calculateNearestValues(valueBigNum);
      if (tempAdjValues.lowerAdjValue) {
        if (!adjacentValues.lowerAdjValue || tempAdjValues.lowerAdjValue.greaterThan(adjacentValues.lowerAdjValue)) {
          adjacentValues.lowerAdjValue = tempAdjValues.lowerAdjValue;
        }
      }

      if (tempAdjValues.upperAdjValue) {
        if (!adjacentValues.upperAdjValue || tempAdjValues.upperAdjValue.lessThan(adjacentValues.upperAdjValue)) {
          adjacentValues.upperAdjValue = tempAdjValues.upperAdjValue;
        }
      }
    }
    return adjacentValues;
  }

  /**
   * Returning the collection in a string format
   */
  public toString(): string {
    return this.ranges.toString();
  }
}
